import {
    AddStaffToTaskCommand, AddStaffToTasksCommand,
    AddTaskBeforeOrAfterCommand,
    CreateAdHocTaskCommand,
    DeleteAdHocTaskCommand,
    DeleteTaskCommand,
    FixRateTaskCommand, LocationModel,
    PlannedEquipmentViewModel, RemoveStaffFromTaskCommand, RemoveStaffFromTasksCommand,
    SetTaskEquipmentCommand,
    SplitAdhocTaskCommand,
    SplitTaskCommand, Staff, StaffViewModel, SwitchTaskBogTypeCommand, SyncTasksCommand,
    TaskCommandResponse,
    TaskTypeViewModel,
    UpdateAdHocTaskCommand,
    UpdateAdHocTaskTimesCommand, UpdateTaskQuantityCommand,
    UpdateTaskTimesCommand,
    UpdateType
} from '@/models/api';
import ClientTaskModel from '@/models/client/client-task';
import PlannedAdHocTasks from '../data/PlannedAdHocTasks';
import Shift from '../data/Shift';
import { curry, curryRight } from 'lodash';
import {
    AddNewTaskRelativeToExisting,
    BringTaskForward,
    DeleteTask,
    DeleteAdHocTask,
    SplitAdHocTask,
    SplitTask,
    AddNewAdHocTask,
    DoNothing,
    UpdateAdHocTask,
    SetTaskEquipment,
    RemoveTaskWithNoSideEffects,
    ClientOnlyRemoveTaskCommand,
    AddStaffToTask,
    RemoveStaffFromTask,
    AddStaffToTasks,
    RemoveStaffFromTasks, RemoveAllStaffFromTasks,
    ClientOnlyTaskCommand, AddNewTaskAtTimePoint, NoOpApiCall, BringAdHocTaskForward
} from '@/lib/services/Location';
import { ClientRowBlastPacketTargetDisplayInformation } from '@/lib/stores/Production/ShiftWindowActualsStore';
import ClientRow from '@/models/client/client-row';
import { AddTaskAtTimePointCommand } from '@/models/client/Commands/add-task-at-time-point-command';
import { RemoveAllStaffFromTasksCommand } from '@/models/client/Commands/remove-all-staff-from-tasks-command';

interface CommandHandler<T> {
    syncType: 'TASK' | 'ADHOC' | 'TASK_THEN_API' | 'NONE';
    syncChangeSetTransformerFactory?: (command: T) => (changeSet: SyncTasksCommand) => SyncTasksCommand;
    clientHandler: (headingTasks: ClientTaskModel[], command: T) => ClientTaskModel[];
    apiHandler: (command: T) => Promise<TaskCommandResponse>;
    updateHeading: boolean;
}

export function NoOpChangeSetTransformerFactory(command: any): (changeSet: SyncTasksCommand) => SyncTasksCommand {
    return cs => cs;
}

function addTaskBeforeOrAfterExistingChangeSetTransformer(command: AddTaskBeforeOrAfterCommand): (changeSet: SyncTasksCommand) => SyncTasksCommand {
    return (changeSet: SyncTasksCommand) => ({
        ... changeSet,
        newTaskCycles: [...command.newTaskCycles],
        tasksWithChangedCycles: [...command.tasksWithChangedCycles]
    });
}

function addTaskAtTimePointExistingChangeSetTransformer(command: AddTaskAtTimePointCommand): (changeSet: SyncTasksCommand) => SyncTasksCommand {
    return (changeSet: SyncTasksCommand) => ({
        ... changeSet,
        newTaskCycles: [...command.newTaskCycles]
    });
}

function switchTaskBogTypeChangeSetTransformer(command: SwitchTaskBogTypeCommand): (changeSet: SyncTasksCommand) => SyncTasksCommand {
    return (changeSet: SyncTasksCommand) => ({
        ... changeSet,
        cycleUpdates: changeSet.cycleUpdates.map(c => {
           if(c.taskLocations.some(t => t.taskId === command.plannedTaskId)) {
                return {
                     ...c,
                     taskLocations: c.taskLocations.map(t => {
                          if (t.taskId === command.plannedTaskId) {
                            return {
                                 ...t,
                                 changedTaskTypeId: command.bogTaskTypeId
                            };
                          }
                          return t;
                     })
                };
           }

           return c;
        })
    });
}

export function GetCommandType(command: any) {
    if (command._type === 'UpdateTaskTimesCommand' && command.updateType === UpdateType.BringForward) {
        return 'BringTaskForwardCommand';
    }

    if (command._type === 'UpdateAdHocTaskTimesCommand' && command.updateType === UpdateType.BringForward) {
        return 'BringAdHocTaskForwardCommand';
    }

    return command._type;
}

export function isTaskCommandResponse(arg: any): arg is TaskCommandResponse {
    return arg && ('planningUpdateTag' in arg);
}

export function GetClientOnlyCommandHandlers(commands: ClientOnlyTaskCommand[]) {
    return commands.map(command=>{
        switch(command.type) {
            case 'RemoveTaskCommand':
            {
                const removeTaskCommand = command as ClientOnlyRemoveTaskCommand;
                return curryRight(RemoveTaskWithNoSideEffects)(removeTaskCommand);
            }
            default:
                throw new Error(`Could not find command type ${command.type}`);
        }
    });
}

export function GetCommandHandlers(
    commandType: string,
    locationInformation: LocationModel,
    departmentId: string,
    equipment: PlannedEquipmentViewModel[],
    staff: Staff[],
    taskTypes: TaskTypeViewModel[],
    blastPacketTargets: ClientRowBlastPacketTargetDisplayInformation[]
) {
    switch (commandType) {
        case 'DeleteTaskCommand':
            return {
                syncType: 'TASK',
                clientHandler: DeleteTask,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<DeleteTaskCommand>;
        case 'DeleteAdHocTaskCommand':
            return {
                syncType: 'ADHOC',
                clientHandler: DeleteAdHocTask,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<DeleteAdHocTaskCommand>;
        case 'AddTaskBeforeOrAfterCommand':
            return {
                syncType: 'TASK',
                clientHandler: curry(AddNewTaskRelativeToExisting)(taskTypes)(blastPacketTargets)(locationInformation),
                syncChangeSetTransformerFactory: addTaskBeforeOrAfterExistingChangeSetTransformer,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<AddTaskBeforeOrAfterCommand>;
        case 'AddTaskAtTimePointCommand':
            return {
                syncType: 'TASK',
                clientHandler: curry(AddNewTaskAtTimePoint)(taskTypes)(blastPacketTargets)(locationInformation)(departmentId),
                syncChangeSetTransformerFactory: addTaskAtTimePointExistingChangeSetTransformer,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<AddTaskAtTimePointCommand>;
        case 'SplitTaskCommand':
            return {
                syncType: 'TASK',
                clientHandler: curry(SplitTask)(taskTypes),
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<SplitTaskCommand>;
        case 'SplitAdhocTaskCommand':
            return {
                syncType: 'ADHOC',
                clientHandler: curry(SplitAdHocTask)(taskTypes),
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<SplitAdhocTaskCommand>;
        case 'CreateAdHocTaskCommand':
            return {
                syncType: 'ADHOC',
                clientHandler: curry(AddNewAdHocTask)(taskTypes),
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<CreateAdHocTaskCommand>;
        case 'UpdateAdHocTaskCommand':
            return {
                syncType: 'ADHOC',
                clientHandler: curry(UpdateAdHocTask)(taskTypes),
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<UpdateAdHocTaskCommand>;
        case 'AddStaffToTaskCommand':
            return {
                syncType: 'NONE',
                clientHandler: curry(AddStaffToTask)(staff),
                apiHandler: Shift.addStaffToTask,
                updateHeading: false,
            } as CommandHandler<AddStaffToTaskCommand>;
        case 'RemoveStaffFromTaskCommand':
            return {
                syncType: 'NONE',
                clientHandler: RemoveStaffFromTask,
                apiHandler: Shift.removeStaffFromTask,
                updateHeading: false,
            } as CommandHandler<RemoveStaffFromTaskCommand>;
        case 'AddStaffToTasksCommand':
            return {
                syncType: 'NONE',
                clientHandler: curry(AddStaffToTasks)(staff),
                apiHandler: Shift.addStaffToTasks,
                updateHeading: false,
            } as CommandHandler<AddStaffToTasksCommand>;
        case 'RemoveStaffFromTasksCommand':
            return {
                syncType: 'NONE',
                clientHandler: RemoveStaffFromTasks,
                apiHandler: Shift.removeStaffFromTasks,
                updateHeading: false,
            } as CommandHandler<RemoveStaffFromTasksCommand>;
        case 'RemoveAllStaffFromTasksCommand':
            return {
                syncType: 'NONE',
                clientHandler: RemoveAllStaffFromTasks,
                apiHandler: NoOpApiCall,
                updateHeading: false,
            } as CommandHandler<RemoveAllStaffFromTasksCommand>;
        case 'SetTaskEquipmentCommand':
            return {
                syncType: 'NONE',
                clientHandler: curry(SetTaskEquipment)(equipment),
                apiHandler: Shift.updateEquipment,
                updateHeading: false,
            } as CommandHandler<SetTaskEquipmentCommand>;
        case 'BringTaskForwardCommand':
            return {
                syncType: 'TASK',
                clientHandler: BringTaskForward,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<UpdateTaskTimesCommand>;
        case 'UpdateTaskTimesCommand':
            return {
                syncType: 'TASK',
                clientHandler: DoNothing,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<UpdateTaskTimesCommand>;
        case 'UpdateTaskQuantityCommand':
            return {
                syncType: 'TASK',
                clientHandler: DoNothing,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<UpdateTaskQuantityCommand>;
        case 'SwitchTaskBogTypeCommand':
            return {
                syncType: 'TASK',
                clientHandler: DoNothing,
                apiHandler: NoOpApiCall,
                syncChangeSetTransformerFactory: switchTaskBogTypeChangeSetTransformer,
                updateHeading: true,
            } as CommandHandler<SwitchTaskBogTypeCommand>;
        case 'BringAdHocTaskForwardCommand':
            return {
                syncType: 'ADHOC',
                clientHandler: BringAdHocTaskForward,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<UpdateAdHocTaskTimesCommand>;
        case 'UpdateAdHocTaskTimesCommand':
            return {
                syncType: 'ADHOC',
                clientHandler: DoNothing,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<UpdateAdHocTaskTimesCommand>;
        case 'FixRateTaskCommand':
            return {
                syncType: 'TASK',
                clientHandler: DoNothing,
                apiHandler: NoOpApiCall,
                updateHeading: true,
            } as CommandHandler<FixRateTaskCommand>;
        default:
            throw new Error(`Could not find command type ${commandType}`);
    }
}
